// src/components/page/block/EditorToolbar.tsx
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import SubjectIcon from "@mui/icons-material/Subject";
import TitleIcon from "@mui/icons-material/Title";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import GridOnIcon from "@mui/icons-material/GridOn";
import GridOffIcon from "@mui/icons-material/GridOff";
import BorderTopIcon from "@mui/icons-material/BorderTop";
import BorderBottomIcon from "@mui/icons-material/BorderBottom";
import BorderLeftIcon from "@mui/icons-material/BorderLeft";
import BorderRightIcon from "@mui/icons-material/BorderRight";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { IconButton, Paper, Divider, Tooltip } from "@mui/material";
import React, { useEffect, useReducer } from "react";
import { useActiveEditor } from "../../../context/activeEditor";

const preventBlur = (e: React.MouseEvent) => e.preventDefault();

export const EditorToolbar: React.FC = () => {
  const { activeEditor } = useActiveEditor();
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  useEffect(() => {
    if (!activeEditor) return;
    activeEditor.on("transaction", forceUpdate);
    activeEditor.on("selectionUpdate", forceUpdate);
    return () => {
      activeEditor.off("transaction", forceUpdate);
      activeEditor.off("selectionUpdate", forceUpdate);
    };
  }, [activeEditor]);

  if (!activeEditor) return null;
  const ed = activeEditor;
  const color = (active: boolean) => (active ? "primary" : "default");
  const inTable = ed.isActive("table");

  return (
    <Paper
      elevation={0}
      square
      onMouseDown={preventBlur}
      sx={{
        width: "100%",
        borderRadius: 0,
        bgcolor: "background.paper",
        borderTop: 1,
        borderColor: "divider",
        display: "flex",
        alignItems: "center",
        gap: 0.25,
        px: 1,
        py: 0.25,
        minHeight: 40,
        flexWrap: "nowrap",
        overflowX: "auto",
      }}
    >
      {/* Group: bold, italic, strike */}
      <IconButton
        size="small"
        color={color(ed.isActive("bold"))}
        onMouseDown={preventBlur}
        onClick={() => ed.chain().focus().toggleBold().run()}
        aria-label="bold"
        sx={{ borderRadius: 1, bgcolor: ed.isActive("bold") ? "action.selected" : "transparent" }}
      >
        <FormatBoldIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        color={color(ed.isActive("italic"))}
        onMouseDown={preventBlur}
        onClick={() => ed.chain().focus().toggleItalic().run()}
        aria-label="italic"
        sx={{ borderRadius: 1, bgcolor: ed.isActive("italic") ? "action.selected" : "transparent" }}
      >
        <FormatItalicIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        color={color(ed.isActive("strike"))}
        onMouseDown={preventBlur}
        onClick={() => ed.chain().focus().toggleStrike().run()}
        aria-label="strike"
        sx={{ borderRadius: 1, bgcolor: ed.isActive("strike") ? "action.selected" : "transparent" }}
      >
        <StrikethroughSIcon fontSize="small" />
      </IconButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />

      {/* Group: h1, h2, paragraph */}
      <IconButton
        size="small"
        color={color(ed.isActive("heading", { level: 1 }))}
        onMouseDown={preventBlur}
        onClick={() => ed.chain().focus().toggleHeading({ level: 1 }).run()}
        aria-label="heading-1"
        sx={{ borderRadius: 1, bgcolor: ed.isActive("heading", { level: 1 }) ? "action.selected" : "transparent" }}
      >
        <TitleIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        color={color(ed.isActive("heading", { level: 2 }))}
        onMouseDown={preventBlur}
        onClick={() => ed.chain().focus().toggleHeading({ level: 2 }).run()}
        aria-label="heading-2"
        sx={{ borderRadius: 1, bgcolor: ed.isActive("heading", { level: 2 }) ? "action.selected" : "transparent" }}
      >
        <TextFieldsIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        color={color(ed.isActive("paragraph"))}
        onMouseDown={preventBlur}
        onClick={() => ed.chain().focus().setParagraph().run()}
        aria-label="paragraph"
        sx={{ borderRadius: 1, bgcolor: ed.isActive("paragraph") ? "action.selected" : "transparent" }}
      >
        <SubjectIcon fontSize="small" />
      </IconButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />

      {/* Group: bulletList, orderedList */}
      <IconButton
        size="small"
        color={color(ed.isActive("bulletList"))}
        onMouseDown={preventBlur}
        onClick={() => ed.chain().focus().toggleBulletList().run()}
        aria-label="bullet-list"
        sx={{ borderRadius: 1, bgcolor: ed.isActive("bulletList") ? "action.selected" : "transparent" }}
      >
        <FormatListBulletedIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        color={color(ed.isActive("orderedList"))}
        onMouseDown={preventBlur}
        onClick={() => ed.chain().focus().toggleOrderedList().run()}
        aria-label="ordered-list"
        sx={{ borderRadius: 1, bgcolor: ed.isActive("orderedList") ? "action.selected" : "transparent" }}
      >
        <FormatListNumberedIcon fontSize="small" />
      </IconButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />

      {/* Group: insert-table */}
      <Tooltip title="Insert table">
        <IconButton
          size="small"
          onMouseDown={preventBlur}
          aria-label="insert-table"
          onClick={() =>
            ed
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run()
          }
          sx={{ borderRadius: 1 }}
        >
          <GridOnIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {inTable && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />

          {/* Group: insert row above/below, col left/right */}
          <Tooltip title="Insert row above">
            <IconButton
              size="small"
              onMouseDown={preventBlur}
              aria-label="add-row-above"
              onClick={() => ed.chain().focus().addRowBefore().run()}
              sx={{ borderRadius: 1 }}
            >
              <BorderTopIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Insert row below">
            <IconButton
              size="small"
              onMouseDown={preventBlur}
              aria-label="add-row-below"
              onClick={() => ed.chain().focus().addRowAfter().run()}
              sx={{ borderRadius: 1 }}
            >
              <BorderBottomIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Insert column left">
            <IconButton
              size="small"
              onMouseDown={preventBlur}
              aria-label="add-col-left"
              onClick={() => ed.chain().focus().addColumnBefore().run()}
              sx={{ borderRadius: 1 }}
            >
              <BorderLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Insert column right">
            <IconButton
              size="small"
              onMouseDown={preventBlur}
              aria-label="add-col-right"
              onClick={() => ed.chain().focus().addColumnAfter().run()}
              sx={{ borderRadius: 1 }}
            >
              <BorderRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />

          {/* Group: merge, split */}
          <Tooltip title="Merge cells">
            <IconButton
              size="small"
              onMouseDown={preventBlur}
              aria-label="merge-cells"
              onClick={() => ed.chain().focus().mergeCells().run()}
              sx={{ borderRadius: 1 }}
            >
              <CallMergeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Split cell">
            <IconButton
              size="small"
              onMouseDown={preventBlur}
              aria-label="split-cell"
              onClick={() => ed.chain().focus().splitCell().run()}
              sx={{ borderRadius: 1 }}
            >
              <CallSplitIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />

          {/* Group: delete row, delete col, delete table */}
          <Tooltip title="Delete row">
            <IconButton
              size="small"
              color="error"
              onMouseDown={preventBlur}
              aria-label="delete-row"
              onClick={() => ed.chain().focus().deleteRow().run()}
              sx={{ borderRadius: 1 }}
            >
              <TableRowsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete column">
            <IconButton
              size="small"
              color="error"
              onMouseDown={preventBlur}
              aria-label="delete-col"
              onClick={() => ed.chain().focus().deleteColumn().run()}
              sx={{ borderRadius: 1 }}
            >
              <ViewColumnIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete table">
            <IconButton
              size="small"
              color="error"
              onMouseDown={preventBlur}
              aria-label="delete-table"
              onClick={() => ed.chain().focus().deleteTable().run()}
              sx={{ borderRadius: 1 }}
            >
              <GridOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Paper>
  );
};
